import React, { useEffect, useState } from 'react';
import styles from './HappyCustomerModal.module.scss';
import Modal from '../../hoc/modal/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomerFaceState } from '../../../app/globals/slice/customerFaceModalSlice/CustomerFaceModalSlice';
import logoMeter from '../../../assets/images/happinessLogo.png';
import Text from '../../atoms/text/Text';
import { usePostMutation } from '../../../services/apiCall';
import endpoints from '../../../api/endpoints';
import { errorMessage, successMessage } from '../../atoms/toast/Toast';
import useIsRtl from '../../../app/helpers/isRtl';
import GreenFace from './assets/GreenFace';
import YellowFace from './assets/YellowFace';
import RedFace from './assets/RedFace';
import { useTranslation } from 'react-i18next';
import getCookie from '../../../app/helpers/getCookie';

const FaceById = { 1: RedFace, 2: YellowFace, 3: GreenFace };
const INITIAL_RATINGS = { ease: null, clarity: null, tech: null };
const INITIAL_FORM = { name: '', feedback: '', mobile: '' };
const faces = [
  { id: 1, Comp: RedFace, label: 'unhappy' },
  { id: 2, Comp: YellowFace, label: 'neutral' },
  { id: 3, Comp: GreenFace, label: 'happy' },
];
const criteria = [
  { key: 'ease', label: 'home.ease_of_use' },
  { key: 'clarity', label: 'home.clarity_of_presentation' },
  { key: 'tech', label: 'home.technical_performance' },
];
const getStep2TitleKey = (selectedFace) => {
  if (selectedFace === 3) return 'home.what_made_you_happy';
  if (selectedFace === 1) return 'home.what_made_you_unhappy';
  return 'home.how_can_we_improve';
};
const HappyCustomerModal = () => {
  const isEnglish = getCookie('googtrans')?.includes('/ar/en');

  const { t } = useTranslation();
  const isRtl = useIsRtl();
  const isOpen = useSelector((state) => state.customerFace.isOpen);
  const dispatch = useDispatch();

  const [post, { isLoading, isSuccess }] = usePostMutation();

  const [step, setStep] = useState(1);
  const [selectedFace, setSelectedFace] = useState(null);
  const [ratings, setRatings] = useState({
    ease: null,
    clarity: null,
    tech: null,
  });

  const [form, setForm] = useState({ name: '', feedback: '' });
  const picked = faces.find((f) => f.id === selectedFace);

  const closeModal = () => {
    dispatch(setCustomerFaceState(false));
    setStep(1);
    setSelectedFace(null);
    setRatings(INITIAL_RATINGS);
    setForm(INITIAL_FORM);
  };

  const handleFacePick = (id) => {
    setSelectedFace(id);
    setStep(2);
  };

  const recordHappiness = async () => {
    if (!selectedFace) return;

    const apiUrl = endpoints.happinessMeter.recordHappiness;
    const payload = {
      apiUrl,
      formData: {
        happyMetter: selectedFace,
        easeOfUse: ratings.ease || 0,
        clarity: ratings.clarity || 0,
        technicalPerformance: ratings.tech || 0,
        tellUsMore: form.feedback || '',
        mobileNumber: form.mobile || '',
      },
    };

    try {
      await post(payload).unwrap();
      successMessage();
    } catch {
      errorMessage();
    }
  };

  useEffect(() => {
    if (isSuccess) {
      window.isHoveringCalendar = false;
      closeModal();
    }
  }, [isSuccess]);
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedFace(null);
      setRatings(INITIAL_RATINGS);
      setForm(INITIAL_FORM);
    }
  }, [isOpen]);
  const allRated = Object.values(ratings).every((v) => !!v);
  const setRating = (key, value) =>
    setRatings((prev) => ({ ...prev, [key]: value }));
  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      dialogClassName={styles.modalClass}
    >
      <div className={styles['customer-modal']}>
        <div className={styles['customer-submodal']}>
          {step === 1 && (
            <div className={styles.step1}>
              <div className={styles.logo}>
                <img src={logoMeter} alt="happiness logo" />
              </div>
              <div className={styles['grid-3']}>
                {faces.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`${styles.face} ${selectedFace === f.id ? styles.active : ''}`}
                    onClick={() => handleFacePick(f.id)}
                    aria-label={`choose ${f.label}`}
                  >
                    <f.Comp className={styles.faceIcon} aria-label={f.label} />
                  </button>
                ))}
              </div>
              <div className={styles.text} style={{ marginTop: '10px' }}>
                <Text
                  title="home.How_was_your_experience"
                  variant="text-bold-italic-20"
                  color="#281E4C"
                />
              </div>
            </div>
          )}

          <div
            className={`${styles.stepTwo} ${step === 2 ? styles.stepTwoShow : ''}`}
            style={{
              display: step === 3 ? 'none' : '',
            }}
          >
            <div
              className={`${styles.selectionHeader} ${isRtl ? styles.selectionHeaderRtl : ''}`}
            >
              {picked ? null : (
                <img className={styles.selectedFace} src={logoMeter} alt="" />
              )}

              <Text
                title={getStep2TitleKey(selectedFace)}
                variant="text-regular-bold-40"
                color="#281E4C"
                className={styles.headerTitle}
              />
            </div>
            <div className={styles.criteriaList}>
              {criteria.map((c) => (
                <div
                  key={c.key}
                  className={`${styles.criteriaRow} ${isRtl ? styles.criteriaRowRtl : ''}`}
                >
                  <Text
                    title={c.label}
                    variant="text-regular-16"
                    color="#008755"
                  />
                  <div className={styles.moods}>
                    {[1, 2, 3].map((val) => {
                      const Comp = FaceById[val];
                      return (
                        <button
                          key={val}
                          type="button"
                          className={`${styles.moodBtn} ${ratings[c.key] === val ? styles.activeMood : ''}`}
                          onClick={() => setRating(c.key, val)}
                          aria-label={`${c.key}-${val}`}
                          title={`${c.key}-${val}`}
                        >
                          <Comp
                            className={styles.moodIcon}
                            active={ratings[c.key] === val}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {step === 3 && (
            <div className={styles.stepThree}>
              <div
                className={`${styles.selectionHeader} ${isRtl ? styles.selectionHeaderRtl3 : ''}`}
              >
                {picked ? null : (
                  <img className={styles.selectedFace} src={logoMeter} alt="" />
                )}

                <Text
                  title={getStep2TitleKey(selectedFace)}
                  variant="text-regular-bold-40"
                  color="#281E4C"
                  className={styles.headerTitle3}
                />
              </div>

              <div className={`${styles.formGroup} ${isRtl ? styles.rtl : ''}`}>
                <textarea
                  className={styles.textarea}
                  value={form.feedback}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, feedback: e.target.value }))
                  }
                  placeholder={t('common.tell_us_more')}
                  rows={4}
                />
                <input
                  className={styles.input}
                  value={form.mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setForm((s) => ({ ...s, mobile: value }));
                  }}
                  placeholder={t('common.your_mobile_number')}
                  inputMode="numeric"
                />
              </div>
            </div>
          )}
          {picked ? (
            <picked.Comp
              className={`${styles.selectedFace} ${step === 2 || step === 3 ? styles.selectedFace2 : ''} ${isRtl ? styles.faceRtl : ''} `}
            />
          ) : null}
        </div>
        <div className={styles.footer}>
          {step === 2 && (
            <div
              className={`${styles.actions} ${isRtl ? styles.actionsRtl : ''}`}
            >
              <button
                type="button"
                className={styles.prevBtn}
                onClick={() => setStep(1)}
              >
                <Text
                  title="Pagination.Previous"
                  variant="text-regular-16"
                  color="#008755"
                  className={styles.text}
                />
              </button>
              <button
                type="button"
                className={styles.prevBtn}
                onClick={() => setStep(3)}
                // disabled={allRated}
              >
                <Text
                  title="Pagination.Next"
                  variant="text-regular-16"
                  color="#008755"
                  className={styles.text}
                />
              </button>
            </div>
          )}

          {step === 3 && (
            <div
              className={`${styles.actions} ${isRtl ? styles.actionsRtl : ''}`}
            >
              <button
                type="button"
                className={styles.prevBtn}
                onClick={() => setStep(2)}
              >
                <Text
                  title="Pagination.Previous"
                  variant="text-regular-16"
                  color="#008755"
                  className={styles.text}
                />
              </button>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={recordHappiness}
                disabled={!selectedFace || isLoading}
              >
                <Text
                  title={
                    isLoading
                      ? 'home.submitting'
                      : isEnglish
                        ? 'Submit'
                        : 'home.submit'
                  }
                  variant="text-regular-16"
                  color="white"
                  className={styles.text}
                />
              </button>
            </div>
          )}

          <p className={styles.subText}>
            <span>A</span>{' '}
            <span className={styles['Dubai-digital']}>Digital Dubai</span>
            <span>initiative</span>
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default HappyCustomerModal;
